import { Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FiActivity,
  FiBell,
  FiCpu,
  FiHome,
  FiLink,
  FiRefreshCw,
  FiShare2,
  FiTrendingUp,
  FiUsers,
  FiUserPlus,
  FiSettings,
  FiMonitor,
  FiServer,
} from "react-icons/fi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import Sidebar from "@/components/Sidebar";
import Topbar from "@/components/Topbar";
import MonitoringBanner from "@/components/MonitoringBanner";
import LoginPage from "@/routes/LoginPage";
import OverviewPage from "@/routes/OverviewPage";
import OLTsPage from "@/routes/OLTsPage";
import ONTsPage from "@/routes/ONTsPage";
import OLTDetailPage from "@/routes/OLTDetailPage";
import ONTDetailPage from "@/routes/ONTDetailPage";
import SubscribersPage from "@/routes/SubscribersPage";
import AlertsPage from "@/routes/AlertsPage";
import SettingsPage from "@/routes/SettingsPage";
import TopologyPage from "@/routes/TopologyPage";
import NetMapPage from "@/routes/NetMapPage";
import GraphsPage from "@/routes/GraphsPage";
import BRASPage from "@/routes/BRASPage";
import IntegrationsPage from "@/routes/IntegrationsPage";
import UsersPage from "@/routes/UsersPage";
import AcceptInvitePage from "@/routes/AcceptInvitePage";
import WallboardPage from "@/routes/WallboardPage";
import SharePage from "@/routes/SharePage";
import MyGraphsPage from "@/routes/MyGraphsPage";

import { AuthContext, useAuth } from "@/lib/auth";
import { me } from "@/lib/api";
import type { Me } from "@/lib/types";
import RequireAuth from "@/lib/RequireAuth";
import { I18nProvider, useI18n } from "@/lib/i18n";
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

export default function App() {
  const [user, setUser] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);

  // Restore session on first mount.
  useEffect(() => {
    (async () => {
      const u = await me();
      setUser(u);
      setReady(true);
    })();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
      <AuthContext.Provider value={{ user, setUser }}>
        {!ready ? (
          <Loading />
        ) : (
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Public: the invited person has no session yet. */}
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            {/* Customer-facing graph share: public by design, the token in the
                path is the only credential. */}
            <Route path="/s/:token" element={<SharePage />} />
            {/* The TV wallboard renders outside the app shell: no sidebar, no
                topbar, it owns the whole screen. Still behind auth. */}
            <Route
              path="/wallboard"
              element={
                <RequireAuth>
                  <WallboardPage />
                </RequireAuth>
              }
            />
            <Route
              path="/*"
              element={
                <RequireAuth>
                  <Shell>
                    <Routes>
                      {/* A port-viewer account has no NOC pages at all: every
                          route resolves to its own graphs, so a typed URL
                          cannot land on a page the API would refuse anyway. */}
                      <Route index element={user?.role === "portviewer" ? <MyGraphsPage /> : <OverviewPage />} />
                      <Route path="my-graphs" element={<MyGraphsPage />} />
                      <Route path="olts" element={<OLTsPage />} />
                      <Route path="olts/:id" element={<OLTDetailPage />} />
                      <Route path="onts" element={<ONTsPage />} />
                      <Route path="onts/:id" element={<ONTDetailPage />} />
                      <Route path="subscribers" element={<SubscribersPage />} />
                      <Route path="alerts" element={<AlertsPage />} />
                      <Route path="topology" element={<TopologyPage />} />
                      <Route path="netmap" element={<NetMapPage />} />
                      <Route path="graphs" element={<GraphsPage />} />
                      <Route path="bras" element={<BRASPage />} />
                      <Route path="integrations" element={<IntegrationsPage />} />
                      <Route path="users" element={<UsersPage />} />
                      <Route path="settings" element={<SettingsPage />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Shell>
                </RequireAuth>
              }
            />
          </Routes>
        )}
      </AuthContext.Provider>
      </I18nProvider>
    </QueryClientProvider>
  );
}

function Loading() {
  const { t } = useI18n();
  return (
    <div className="flex h-full items-center justify-center text-slate-500">{t("app.loading")}</div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  // Drawer closes on route change and Escape; below md only (md+ it is a static column).
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <div className="flex h-full flex-col">
      <Topbar menuOpen={open} onMenu={() => setOpen((v) => !v)} />
      {user?.role !== "portviewer" && <MonitoringBanner />}
      <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[13.5rem_1fr]">
      <Sidebar
        open={open}
        onClose={() => setOpen(false)}
        items={user?.role === "portviewer" ? [
          { to: "/",            label: t("myGraphs.title"),   icon: <FiTrendingUp /> },
        ] : [
          { to: "/",            label: t("nav.overview"),     icon: <FiHome /> },
          { to: "/alerts",      label: t("nav.alerts"),       icon: <FiBell /> },
          { to: "/wallboard",   label: t("nav.wallboard"),    icon: <FiMonitor /> },
          { to: "/olts",        label: t("nav.olts"),         icon: <FiCpu />,        group: t("nav.group.network") },
          { to: "/onts",        label: t("nav.onts"),         icon: <FiActivity />,   group: t("nav.group.network") },
          { to: "/topology",    label: t("nav.topology"),     icon: <FiLink />,       group: t("nav.group.network") },
          { to: "/netmap",      label: t("nav.netmap"),       icon: <FiShare2 />,     group: t("nav.group.network") },
          { to: "/graphs",      label: t("nav.graphs"),       icon: <FiTrendingUp />, group: t("nav.group.network") },
          { to: "/bras",        label: t("nav.bras"),         icon: <FiServer />,     group: t("nav.group.network") },
          { to: "/subscribers", label: t("nav.subscribers"),  icon: <FiUsers />,      group: t("nav.group.operations") },
          { to: "/integrations",label: t("nav.integrations"), icon: <FiRefreshCw />,  group: t("nav.group.operations") },
          { to: "/users",       label: t("nav.users"),        icon: <FiUserPlus />,   group: t("nav.group.admin"), adminOnly: true },
          { to: "/settings",    label: t("nav.settings"),     icon: <FiSettings />,   group: t("nav.group.admin") },
        ]}
      />
        <main className="console-bg min-w-0 overflow-auto p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}
